import React, { useState } from 'react';
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, ComposedChart } from 'recharts';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, 
  ShoppingCart, ArrowUpRight, ArrowDownRight,
  Clock, Target, Zap, AlertCircle, Award
} from 'lucide-react';

import { salesTrends, liveMetrics, topPerformers } from '../data/mockData';

const MetricCard = ({ title, value, change, icon: Icon, color, secondaryMetric }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
  >
    <div className="flex items-center justify-between">
      <div className={`p-2 bg-${color}-50 rounded-lg`}>
        <Icon className={`w-6 h-6 text-${color}-500`} />
      </div>
      <div className={`flex items-center gap-1 text-sm font-medium ${
        parseFloat(change) >= 0 ? 'text-green-600' : 'text-red-600'
      }`}>
        {parseFloat(change) >= 0 ? (
          <ArrowUpRight className="w-4 h-4" />
        ) : (
          <ArrowDownRight className="w-4 h-4" />
        )}
        {Math.abs(parseFloat(change))}%
      </div>
    </div>
    <div className="mt-4">
      <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
      <p className="text-sm text-gray-500 mt-1">{title}</p>
    </div>
    {secondaryMetric && (
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">{secondaryMetric.label}</span>
          <span className="font-medium text-gray-900">{secondaryMetric.value}</span>
        </div>
      </div>
    )}
  </motion.div>
);

const LiveSalesWidget = ({ data }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <div className="flex items-center justify-between mb-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Live Sales</h3>
        <p className="text-sm text-gray-500">Real-time transaction data</p>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <span className="text-sm text-gray-500">Live</span>
      </div>
    </div>
    
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-500">Today's Sales</p>
          <h4 className="text-xl font-semibold text-gray-900 mt-1">
            {data.todaySales.toLocaleString('en-SA', {
              style: 'currency',
              currency: 'SAR'
            })}
          </h4>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-500">Active Stores</p>
          <h4 className="text-xl font-semibold text-gray-900 mt-1">
            {data.activeStores}/{data.totalStores}
          </h4>
        </div>
      </div>

      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.realtimeData}>
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="#10B981" 
              strokeWidth={2}
              dot={false}
            />
            <XAxis dataKey="time" />
            <YAxis />
            <Tooltip />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>
);

const TopPerformersWidget = ({ data }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performers</h3>
    <div className="space-y-4">
      {data.map((store, index) => (
        <div key={store.id} className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <span className="text-sm font-semibold text-gray-600">{index + 1}</span>
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">{store.name}</h4>
            <p className="text-sm text-gray-500">{store.location}</p>
          </div>
          <div className="text-right">
            <div className="font-medium text-gray-900">
              {store.sales.toLocaleString('en-SA', {
                style: 'currency',
                currency: 'SAR'
              })}
            </div>
            <div className={`text-sm ${
              store.growth >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {store.growth >= 0 ? '+' : ''}{store.growth}%
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-SA', {
    month: 'short',
    day: 'numeric'
  });
};

const formatCurrency = (value) => {
  return value.toLocaleString('en-SA', {
    style: 'currency',
    currency: 'SAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
        <p className="text-sm font-medium text-gray-900">{formatDate(label)}</p>
        <div className="mt-2 space-y-1">
          <p className="text-sm text-blue-600">
            Sales: {formatCurrency(payload[0].value)}
          </p>
          <p className="text-sm text-green-600">
            Transactions: {payload[1].value.toLocaleString()}
          </p>
        </div>
      </div>
    );
  }
  return null;
};

const OverviewTab = ({ timeRange }) => {
  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Net Sales (MTD)"
          value="SAR 12,458,932"
          change="15.2"
          icon={DollarSign}
          color="blue"
          secondaryMetric={{
            label: "Daily Average",
            value: "SAR 415,297"
          }}
        />
        <MetricCard
          title="Transactions"
          value="84,729"
          change="8.5"
          icon={ShoppingCart}
          color="green"
          secondaryMetric={{
            label: "Avg Ticket",
            value: "SAR 147"
          }}
        />
        <MetricCard
          title="Customer Count"
          value="62,184"
          change="12.3"
          icon={Users}
          color="purple"
          secondaryMetric={{
            label: "Return Rate",
            value: "24.5%"
          }}
        />
        <MetricCard
          title="Peak Hour Sales"
          value="SAR 42,891"
          change="5.8"
          icon={Zap}
          color="amber"
          secondaryMetric={{
            label: "Peak Time",
            value: "13:00 - 14:00"
          }}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Live Sales Widget - Takes up 2 columns */}
        <div className="lg:col-span-2">
          <LiveSalesWidget data={liveMetrics} />
        </div>

        {/* Top Performers Widget */}
        <div>
          <TopPerformersWidget data={topPerformers} />
        </div>
      </div>

      {/* Sales Trends */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Sales Trends</h3>
            <p className="text-sm text-gray-500">Historical performance analysis</p>
          </div>
          <select
            className="rounded-lg border border-gray-200 text-sm p-2"
            defaultValue="30d"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last Quarter</option>
            <option value="1y">Last Year</option>
          </select>
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={salesTrends.daily}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              tick={{ fill: '#6B7280', fontSize: 12 }}
            />
            <YAxis 
              yAxisId="left"
              tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
              tick={{ fill: '#6B7280', fontSize: 12 }}
            />
            <YAxis 
              yAxisId="right" 
              orientation="right"
              tickFormatter={(value) => value.toLocaleString()}
              tick={{ fill: '#6B7280', fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              yAxisId="left"
              dataKey="sales"
              fill="#3B82F6"
              opacity={0.8}
              radius={[4, 4, 0, 0]}
              name="Sales"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="transactions"
              stroke="#10B981"
              strokeWidth={2}
              dot={{ r: 4 }}
              name="Transactions"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OverviewTab; 